import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { Dispatch, SetStateAction } from 'react';
import useSWR from 'swr';
import { FormPart } from '@modules/shared/components/FormPart';
import { statesList } from '@shared/constants/statesList';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { ShadowDivider } from '@shared/styledComponents';
import { getOptions } from '@shared/commonFunctions';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { CommonButtonWrapper, ScrollPageContent } from '@styles/CommonStyles';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { ASSET_TYPE_OPTIONS } from '@shared/constants/lists';
import { TextInputField } from '@shared/fields/TextInputField';
import { ProductType, ProviderType, RuleType } from '../types';

interface IProductRuleFormProps extends Partial<FormikProps<RuleType>> {
  providerId: string;
  onChangeProvider: Dispatch<SetStateAction<string>>;
  handleClose: () => void;
  providers?: ProviderType[];
  productTypes?: ProductType[];
}

type RuleVariable = {
  displayCode: string;
  displayName: string;
  id: string;
};

const BORDER = '1px solid #e0e0e0';

export const ProductRulesForm = ({
  values,
  providers = [],
  providerId,
  productTypes = [],
  onChangeProvider,
  handleClose,
  setFieldValue,
}: IProductRuleFormProps) => {
  const { data: ruleVariables } = useSWR<RuleVariable[]>('/product-service/api/providers/rulevariables');

  const onChangeProviderId = (value: string) => {
    setFieldValue?.('productTypeId', '');
    onChangeProvider(value);
  };

  const formConfig = [
    {
      width: '50%',
      name: 'providerId',
      label: 'Provider',
      options: getOptions(providers),
      component: SelectAutocompleteField,
      onChange: onChangeProviderId,
    },
    {
      width: '50%',
      name: 'productTypeId',
      label: 'Product',
      key: providerId + productTypes.length,
      options: getOptions(productTypes),
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'startDate',
      label: 'From',
      component: SelectDateField,
    },
    {
      width: '50%',
      name: 'endDate',
      label: 'To',
      component: SelectDateField,
    },
    {
      width: '50%',
      name: 'ilendingCostAmount',
      label: 'Hello Refi Cost Amount',
      key: values?.ilendingCostFormula,
      disabled: !!values?.ilendingCostFormula,
      component: NumberInputField,
      decimalScale: 2,
      removeThousandSeparator: true,
    },
    {
      width: '50%',
      name: 'ilendingCostFormula',
      label: 'Hello Refi Cost Formula',
      key: values?.ilendingCostAmount,
      disabled: !!values?.ilendingCostAmount,
    },
    {
      width: '50%',
      name: 'packAmount',
      label: 'Pack Amount',
      component: NumberInputField,
      key: values?.packFormula,
      disabled: !!values?.packFormula,
      decimalScale: 2,
      removeThousandSeparator: true,
    },
    {
      width: '50%',
      name: 'packFormula',
      label: 'Pack Cost Formula',
      key: values?.packAmount,
      disabled: !!values?.packAmount,
    },
    {
      width: '50%',
      name: 'markupAmount',
      component: NumberInputField,
      key: values?.markupFormula,
      disabled: !!values?.markupFormula,
      decimalScale: 2,
      removeThousandSeparator: true,
    },
    {
      width: '50%',
      name: 'markupFormula',
      label: 'Mark Cost Formula',
      key: values?.markupAmount,
      disabled: !!values?.markupAmount,
    },
    {
      width: '50%',
      name: 'state',
      options: statesList,
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'assetTypes',
      label: 'Asset Type',
      options: ASSET_TYPE_OPTIONS,
      component: SelectAutocompleteField,
    },
    {
      width: '50%',
      name: 'minTerm',
      component: NumberInputField,
    },
    {
      width: '50%',
      name: 'maxTerm',
      component: NumberInputField,
    },
    {
      width: '50%',
      name: 'minMileage',
      component: NumberInputField,
    },
    {
      width: '50%',
      name: 'maxMileage',
      component: NumberInputField,
    },
    {
      width: '50%',
      name: 'minYear',
      component: NumberInputField,
    },
    {
      width: '50%',
      name: 'maxYear',
      component: NumberInputField,
    },
    {
      width: '50%',
      name: 'planName',
      component: TextInputField,
    },
  ];

  return (
    <>
      <ShadowDivider />

      <ScrollPageContent>
        <Grid
          container
          spacing={12}
        >
          <Grid
            item
            md={6}
          >
            <FormPart config={formConfig} />
          </Grid>

          <Grid
            item
            md={6}
            paddingBottom={5}
          >
            <Typography mb={'20px'}>Formula Variables</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold', border: BORDER }}>Code</TableCell>
                  <TableCell style={{ fontWeight: 'bold', border: BORDER }}>Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ruleVariables?.map((rule: RuleVariable, index: number) => {
                  return (
                    <TableRow key={index}>
                      <TableCell sx={{ border: BORDER }}>{rule.displayCode}</TableCell>
                      <TableCell sx={{ border: BORDER }}>{rule.displayName}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
      </ScrollPageContent>
      <CommonButtonWrapper
        direction="row"
      >
        <CancelButton onClick={handleClose} />
        <SaveButton />
      </CommonButtonWrapper>
    </>
  );
};
